import { useSelector } from 'react-redux'
import { fromUnixTime } from 'date-fns'
import keyBy from 'lodash/fp/keyBy'
import { Box, Typography } from '@mui/material'
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import { eventsLogs, type EventsLogsListResponse } from '@fireflyhealth/api'
import type { SvgIconComponent } from '@mui/icons-material'
import { PatientUser } from '~/shared/types'
import { Provider } from '~/entities/provider'
import { useQuery } from '~/shared/hooks/query'
import { InlineLoader, SectionCard, type Tone } from '~/shared/ui'
import { formatDateWithDay } from '~/shared/lib/date'
import { useMemberTimeline } from './useMemberTimeline'
import MemberJourneyTimeline from './MemberJourneyTimeline'

type Event = EventsLogsListResponse[number]

const TIMELINE_TYPES = ['communication_sent', 'mailer_status_change'] as const

// Same status semantics as the legacy DirectMailerListItem: anything outside
// this set renders as an error.
const MAILER_KNOWN_STATUSES = ['sent', 'acknowledged', 'requested'] as const

interface TimelineRow {
  tone: Tone
  Icon: SvgIconComponent
  iconTitle: string
  title: string
  titleDetail?: string
  sub: string
  date: string
}

interface PatientDetailTimelineProps {
  patient: PatientUser
}

/**
 * Timeline tab. Behind `crm.member_timeline` it renders the cross-domain member journey
 * composed by the CRM BFF; with the flag off it renders the outbound-communication log below —
 * push notifications sent and physical-mail status changes, straight off `eventsLogs`.
 */
const PatientDetailTimeline = ({ patient }: PatientDetailTimelineProps) => {
  const memberTimelineEnabled = useMemberTimeline()
  if (memberTimelineEnabled) {
    return <MemberJourneyTimeline patient={patient} />
  }
  return <LegacyEventLogTimeline patient={patient} />
}

/**
 * The pre-journey-timeline tab: tone-tiled rows for the two `eventsLogs` types the tab has
 * always shown, inside a single card.
 */
const LegacyEventLogTimeline = ({ patient }: PatientDetailTimelineProps) => {
  const providers = useSelector((state: { providers: Provider[] }) => state.providers)
  const providersById = keyBy('id', providers)

  const getProviderName = (providerId: number | null | undefined) => {
    const provider = providerId ? providersById[providerId] : null
    return provider ? `${provider.first_name} ${provider.last_name}` : 'System'
  }

  const params = { user: patient.id, type: [...TIMELINE_TYPES] }
  const { data: events, isPending: isLoading } = useQuery(
    eventsLogs.listQueryKey(params),
    async () => (await eventsLogs.list({ params })).data,
    {},
    { error: 'Failed to load patient timeline' },
  )

  if (isLoading || events == null) {
    return <InlineLoader />
  }

  const rowFor = (event: Event): TimelineRow | null => {
    const { metadata } = event
    if ('communication_type' in metadata && metadata.communication_type === 'push_notification') {
      let abbreviatedMessageText = metadata.message_text.substring(0, 47)
      if (abbreviatedMessageText.length < metadata.message_text.length) {
        abbreviatedMessageText += '...'
      }
      return {
        tone: 'info',
        Icon: NotificationsNoneRoundedIcon,
        iconTitle: 'Push Notification',
        title: metadata.message_title,
        titleDetail: abbreviatedMessageText,
        sub: `Push notification · By ${getProviderName(metadata.sent_by_provider_id)}`,
        date: formatDateWithDay(fromUnixTime(metadata.timestamp)),
      }
    }
    if ('mail_type' in metadata && event.type === 'mailer_status_change') {
      const isStatusError = !MAILER_KNOWN_STATUSES.some(status => status === metadata.status)
      const tone: Tone = isStatusError
        ? 'error'
        : metadata.status === 'requested'
          ? 'warning'
          : 'success'
      const status = metadata.status.charAt(0).toUpperCase() + metadata.status.slice(1)
      return {
        tone,
        Icon: isStatusError ? ErrorOutlineRoundedIcon : MailOutlineRoundedIcon,
        iconTitle: 'Direct Mailer',
        title: metadata.mail_type,
        sub: `Direct mailer · ${status} by ${getProviderName(metadata.updated_by)}`,
        date: formatDateWithDay(fromUnixTime(metadata.timestamp)),
      }
    }
    return null
  }

  const rows = events.map(rowFor).filter((row): row is TimelineRow => row !== null)

  return (
    <Box sx={{ px: 3, pt: 1.5, pb: 5 }} data-testid="patient-timeline-events">
      <Typography sx={{ fontSize: '1.25rem', color: 'text.secondary', mb: 1 }}>
        {rows.length} {rows.length === 1 ? 'event' : 'events'}
      </Typography>
      {rows.length > 0 && (
        <SectionCard disablePadding>
          {rows.map((row, i) => (
            <Box
              key={i}
              sx={theme => ({
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.75,
                px: 2,
                py: 1.75,
                '& + &': { borderTop: `1px solid ${theme.palette.divider}` },
              })}
            >
              <Box
                title={row.iconTitle}
                sx={theme => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 38,
                  height: 38,
                  borderRadius: 1,
                  flex: '0 0 auto',
                  bgcolor: theme.palette.tones[row.tone].bg,
                  color: theme.palette.tones[row.tone].fg,
                })}
              >
                <row.Icon sx={{ fontSize: '2rem' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  title={row.titleDetail ?? row.title}
                  sx={{ fontSize: '1.4rem', fontWeight: 500, color: 'text.primary' }}
                >
                  {row.title}
                  {row.titleDetail && (
                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                      &nbsp;{row.titleDetail}
                    </Box>
                  )}
                </Typography>
                <Typography sx={{ mt: 0.25, fontSize: '1.25rem', color: 'text.secondary' }}>
                  {row.sub}
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: '1.2rem', color: 'text.secondary', whiteSpace: 'nowrap', pt: 0.25 }}
              >
                {row.date}
              </Typography>
            </Box>
          ))}
        </SectionCard>
      )}
    </Box>
  )
}

export default PatientDetailTimeline
